import { ROW_STATUS, UNUSABLE_REASON } from './bulkLotNdcStatus';

/**
 * How the review grid renders a classified row: its badge text and its colour.
 *
 * Split from the classifier because it is presentation, not judgement — nothing in the classifier
 * reads these, only the grid and the filter bar do — and because the classifier is at the
 * 450-line cap. The dependency runs one way: this file knows the vocabulary, the classifier does
 * not know this file.
 */

/**
 * The badge text for each status.
 *
 * Presentation, but it lives here rather than in the grid because the filter dropdown offers the
 * same statuses and the two must never drift — a filter reading "Expired lot" that matches rows
 * badged something else is worse than either wording alone.
 */
export const STATUS_LABEL = {
  [ROW_STATUS.UNUSABLE_LOT]: 'Expired lot',
  [ROW_STATUS.NO_USABLE_LOT]: 'No usable lot',
  [ROW_STATUS.NO_LOT]: 'No lot',
  [ROW_STATUS.NO_NDC]: 'No NDC',
  [ROW_STATUS.NO_CATALOG]: 'No inventory',
  [ROW_STATUS.NO_ALTERNATIVE]: 'No alternative',
  // "Recommended", not "As allocated": nothing is allocated at this point — the commit records the
  // lot on the fill without reserving stock. What the row is really saying is "this is the lot we
  // would use", which is a recommendation the user is free to override.
  [ROW_STATUS.AS_ALLOCATED]: 'Recommended',
  [ROW_STATUS.CHANGED]: 'Changed',
  [ROW_STATUS.SHORTFALL_COMMITTED]: 'Claim confirmed',
  [ROW_STATUS.MANUAL_LOT_COMMITTED]: 'Hand-recorded',
  [ROW_STATUS.ALREADY_DISPENSED]: 'Already dispensed',
  [ROW_STATUS.LOCKED_FOR_COUNT]: 'Locked for count',
};

/**
 * One status covers three ways a recorded lot can be unusable, so the badge narrows it down.
 * "Expired lot" on a lot that was actually deleted from inventory would send the user looking for
 * an expiry date that is not there.
 */
export const statusLabelFor = ({ status, unusableReason }) => {
  if (status === ROW_STATUS.UNUSABLE_LOT) {
    // POM-6119: the lot is on the shelf and in date, so neither 'Lot expired' nor the
    // 'Lot unavailable' fallback describes it — and the chip is what gets read first.
    if (unusableReason === UNUSABLE_REASON.SHORT_FOR_FILL) return 'Partial';
    if (unusableReason !== UNUSABLE_REASON.EXPIRED) return 'Lot unavailable';
  }

  return STATUS_LABEL[status] ?? status;
};

/** Why a row is being held back, phrased for the acknowledgement panel's heading. */
/**
 * Colour per status, used for both the badge and the row's left stripe.
 *
 * Recommended is coloured too — green, so a good row reads as good at a glance rather than
 * blending in with the plain informational rows. Statuses absent from this map (already
 * dispensed, locked for count) stay uncoloured: they get no stripe and a plain badge.
 */
export const STATUS_COLOR = {
  [ROW_STATUS.UNUSABLE_LOT]: '#c62828',
  [ROW_STATUS.NO_NDC]: '#c62828',
  [ROW_STATUS.NO_USABLE_LOT]: '#e65100',
  [ROW_STATUS.NO_LOT]: '#f57f17',
  [ROW_STATUS.NO_CATALOG]: '#f57f17',
  [ROW_STATUS.CHANGED]: '#1565c0',
  [ROW_STATUS.SHORTFALL_COMMITTED]: '#e65100',
  [ROW_STATUS.MANUAL_LOT_COMMITTED]: '#e65100',
  [ROW_STATUS.AS_ALLOCATED]: '#2e7d32',
};

/**
 * The chip's colour, asked the same way its label is.
 *
 * POM-6119 — a map keyed on status alone cannot tell a short lot from an expired one, and they are
 * not the same news: an expired lot is a problem to fix, a short lot now splits by itself and
 * prints. Red said "stop" about an ordinary outcome. Amber here, matching the two committed-claim
 * statuses, because what they have in common is that a claim is involved.
 */
export const statusColorFor = ({ status, unusableReason }) => {
  if (status === ROW_STATUS.UNUSABLE_LOT && unusableReason === UNUSABLE_REASON.SHORT_FOR_FILL) {
    return STATUS_COLOR[ROW_STATUS.SHORTFALL_COMMITTED];
  }

  return STATUS_COLOR[status];
};
